import { useState, useReducer } from 'react';
import { SimilarRolesResponse } from '../../../api';

// Define the workflow steps
export enum WorkflowStep {
  Main,
  Step1,
  Step2,
  Step3,
}

// Define the state for the workflow
interface WorkflowState {
  step1Data: ClientProposalDescriptionInformation | undefined;
  step2Data: ClientProposalRoleInformation | undefined;
  step3Data: ClientProposalRelevantInformation | undefined;
  isLoading: boolean;
}

interface ClientProposalData {
  step1Data: ClientProposalDescriptionInformation;
  step2Data: ClientProposalRoleInformation;
  step3Data: ClientProposalRelevantInformation;
}

export interface ClientProposalDescriptionInformation {
  consultingTeam: ConsultantBio[];
  type: 'file' | 'description';
  file: File | null;
  description: string;
  filename?: string;
}

export interface ConsultantBio {
  name: string;
  id: string;
}

export interface ClientProposalRoleInformation {
  organisation: string;
  roleTitle: string;
  clientContact: string;
  contactTitle: string;
  address: string;
  suburb: string;
  state: string;
  postCode: string;
  totalRemuneration: string;
  roleType: string;
  sector: string;
  noticePeriod: string;
  replacementGuarantee: string;
  rate: string;
  confirmationOfAssignmentPeriod: string;
  confirmationOfAssignmentAmount: string;
  confirmationOfAssignmentFrom: string;
  confirmationOfAssignmentTo: string;
  candidateSearchPeriod: string;
  candidateSearchAmount: string;
  candidateSearchFrom: string;
  candidateSearchTo: string;
  interviewsPeriod: string;
  interviewsAmount: string;
  interviewsFrom: string;
  interviewsTo: string;
  referencingPeriod: string;
  referencingAmount: string;
  referencingFrom: string;
  referencingTo: string;
  noticePeriodFrom: string;
  noticePeriodTo: string;
  preferredStartDate: string;
  ratePercentage: string;
  flatFee: string;
}
export type ClientProposalRelevantInformation = {
  similiarRoles: SimiliarRole[];
  similiarSectors: SimiliarRole[];
  similiarOrganisations: SimiliarRole[];
  similiarCustom: SimiliarCustom | undefined;
};

export type SimiliarRole = {
  id?: string;
  role: string;
  organization: string;
  partitionKey: string;
  rowKey: string;
};

export type SimiliarCustom = {
  name: string;
  description: string;
  similiar: SimiliarRole[];
};

// Define actions for the workflow
type WorkflowAction =
  | { type: 'SET_STEP1_DATA'; payload: ClientProposalDescriptionInformation }
  | { type: 'SET_STEP2_DATA'; payload: ClientProposalRoleInformation; roles?: SimilarRolesResponse }
  | { type: 'SET_STEP3_DATA'; payload: ClientProposalRelevantInformation }
  | { type: 'RESET' }
  | { type: 'SET_LOADING'; payload: boolean };

// Create a reducer to manage the workflow state
const workflowReducer = (state: WorkflowState, action: WorkflowAction): WorkflowState => {
  switch (action.type) {
    case 'SET_STEP1_DATA':
      return { ...state, step1Data: action.payload };
    case 'SET_STEP2_DATA':
      // If roles are provided and have valid data, add them to the state
      if (action.roles?.data?.similarRoles) {
        return {
          ...state,
          step2Data: action.payload,
          step3Data: {
            ...state.step3Data,
            similiarRoles: action.roles.data.similarRoles.map(role => ({
              ...role,
              organization: role.organization,
              id: role.id,
              rowKey: role.rowKey,
              partitionKey: role.partitionKey,
            })),
            similiarSectors:
              action.roles.data.similarSectors?.map(role => ({
                ...role,
                organization: role.organization,
                id: role.id,
                rowKey: role.rowKey,
                partitionKey: role.partitionKey,
              })) || [],
            similiarOrganisations:
              action.roles.data.similarOrganisations?.map(role => ({
                ...role,
                organization: role.organization,
                id: role.id,
                rowKey: role.rowKey,
                partitionKey: role.partitionKey,
              })) || [],
            similiarCustom: state.step3Data?.similiarCustom,
          },
        };
      } else {
        // If no roles data, initialize with empty arrays
        return {
          ...state,
          step2Data: action.payload,
          step3Data: {
            similiarRoles: [],
            similiarSectors: [],
            similiarOrganisations: [],
            similiarCustom: state.step3Data?.similiarCustom,
          },
        };
      }
    case 'SET_STEP3_DATA':
      return { ...state, step3Data: action.payload, isLoading: true };
    case 'RESET':
      return { step1Data: undefined, step2Data: undefined, step3Data: undefined, isLoading: false };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
};

// Custom hook to manage the workflow
export const useWorkflow = () => {
  const [currentStep, setCurrentStep] = useState<WorkflowStep>(WorkflowStep.Main);
  const [state, dispatch] = useReducer(workflowReducer, {
    step1Data: undefined,
    step2Data: undefined,
    step3Data: undefined,
    isLoading: false,
  });

  const goToNextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, WorkflowStep.Step3) as WorkflowStep);
  };

  const goToPreviousStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, WorkflowStep.Main) as WorkflowStep);
  };

  const resetWorkflow = () => {
    setCurrentStep(WorkflowStep.Main);
    dispatch({ type: 'RESET' });
  };

  const onStepClick = (step: WorkflowStep) => {
    setCurrentStep(step);
  };

  const goToStep = (step: WorkflowStep) => {
    setCurrentStep(step);
  };

  const handleStep3Submit = async (data: ClientProposalRelevantInformation) => {};

  return {
    currentStep,
    state,
    dispatch,
    goToNextStep,
    goToPreviousStep,
    resetWorkflow,
    onStepClick,
    handleStep3Submit,
    isLoading: state.isLoading,
    goToStep,
  };
};
